import { useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useViewport } from '@/hooks/dom/use-viewport';
import type { CstretchmodalController } from './Cstretchmodal.types';
import {
  buildCstretchmodalStyleModel,
  buildCstretchmodalViewportModel,
} from './cstretchmodalModel';
import { stopCstretchmodalPropagation } from './cstretchmodalEvents';
import { useCstretchmodalCloseHandler } from './useCstretchmodalCloseHandler';
import { useCstretchmodalDebugLogger } from './useCstretchmodalDebugLogger';
import { useCstretchmodalLifecycle } from './useCstretchmodalLifecycle';
import { useCstretchmodalRegistration } from './useCstretchmodalRegistration';

interface UseCstretchmodalControllerInput {
  compactAsMobile: boolean;
  contentContainerStyle?: CSSProperties;
  debugContext?: Record<string, unknown>;
  debugName?: string;
  modalWrapperStyle?: CSSProperties;
  onClose?: () => void;
  overlayPortalTargetId?: string;
  padding: number | string;
  showBackButton: boolean;
  style?: CSSProperties;
  visible: boolean;
}

export function useCstretchmodalController({
  compactAsMobile,
  contentContainerStyle,
  debugContext,
  debugName,
  modalWrapperStyle,
  onClose,
  overlayPortalTargetId,
  padding,
  showBackButton,
  style,
  visible,
}: UseCstretchmodalControllerInput): CstretchmodalController {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const canClose = Boolean(onClose);
  const { viewport } = useViewport();
  const viewportModel = useMemo(() => buildCstretchmodalViewportModel({
    compactAsMobile,
    padding,
    viewport,
  }), [compactAsMobile, padding, viewport]);
  const debugLogger = useCstretchmodalDebugLogger();
  const handleClose = useCstretchmodalCloseHandler({
    debugContext,
    debugLogger,
    debugName,
    onClose,
    visible,
  });
  const { modalId, registration } = useCstretchmodalRegistration({ handleClose, visible });
  const isTop = registration.isTopOpen;

  useCstretchmodalLifecycle({
    canClose, compactAsMobile, debugContext, debugLogger, debugName, handleClose, isTop,
    modalId, overlayPortalTargetId, showBackButton, viewport, viewportModel, visible,
  });

  const styleModel = buildCstretchmodalStyleModel({
    contentContainerStyle,
    contentZIndex: registration.contentZIndex,
    modalWrapperStyle,
    style,
    viewportModel,
  });

  return {
    canClose,
    completeExit: registration.completeExit,
    handleClose,
    stopPropagation: stopCstretchmodalPropagation,
    styleModel,
    surfaceRef,
  };
}
